import { useDashboard } from '@/hooks/useDashboard';
import { useDashboardStore } from '@/stores/dashboardStore';
import { activeFilterCount } from '@/utils/formatters';
import KPICards from '@/components/KPICards';
import ChartCard from '@/components/charts/ChartCard';
import DrillTable from '@/components/tables/DrillTable';
import type { TabData, AbnormalData, DrillTable as DrillTableData } from '@/types/dashboard';

function getAbnormalRows(abnormal?: AbnormalData): Record<string, unknown>[] {
  if (!abnormal?.data) return [];
  if (Array.isArray(abnormal.data)) return abnormal.data;
  return Object.values(abnormal.data)
    .filter((v): v is Record<string, unknown>[] => Array.isArray(v))
    .flat();
}

/** 去掉标题中的下钻提示括号（如「（点击人员下钻到每日明细）」），避免干扰源/明细判断 */
function stripDrillHint(title: string): string {
  return title
    .replace(/[（(][^）)]*(?:点击|下钻|查看)[^）)]*[）)]/g, '')
    .trim();
}

/** 根据 drill 列表，推断哪些明细表只作为下钻目标、不应默认平铺展示。
 * 只要 tab 里同时存在汇总/排名/预警表和对应明细目标表，就把明细目标表隐藏。 */
function getHiddenDrillTitles(drills: DrillTableData[]): string[] {
  const hidden: string[] = [];
  const hasSummary = drills.some((d) => {
    const clean = stripDrillHint(d.title);
    return /排名|汇总|预警/.test(clean) && !/明细|每日/.test(clean);
  });
  if (hasSummary) {
    if (drills.some((d) => d.title.includes('客户明细'))) hidden.push('客户明细');
    if (drills.some((d) => d.title.includes('人员每日明细'))) hidden.push('人员每日明细');
  }
  return hidden;
}

const PERSON_COLS = ['人员', '姓名', '执行人'];

/** 判断源表是否属于汇总/排名/预警类（有下钻入口），而非明细目标表 */
function isSourceDrillable(title: string): boolean {
  const clean = stripDrillHint(title);
  return /排名|汇总|预警/.test(clean) && !/明细|每日/.test(clean);
}

/** 根据源表格标题、点击列和值，定位下钻目标数据（弹窗模式） */
function findDrillTarget(
  data: TabData,
  sourceTitle: string,
  column: string,
  value: unknown,
): { title: string; columns: string[]; rows: Record<string, unknown>[]; block?: string; filterKey?: string } | null {
  if (!value) return null;
  const v = String(value);
  const drills = data.drills || [];

  // 异常定位雷达：省办 → 客户异常排名（abnormal 有省办列）
  if (sourceTitle.includes('省办异常排名') && column === '省办') {
    const rows = getAbnormalRows(data.abnormal).filter(
      (r) => String(r['省办'] ?? '') === v,
    );
    return {
      title: `${v} - 营业所+客户明细`,
      columns: data.abnormal?.columns || (rows.length > 0 ? Object.keys(rows[0]) : []),
      rows,
      block: data.abnormal?.block,
      filterKey: '省办',
    };
  }

  // 异常定位雷达：人员异常排名点击执行人后，由于 abnormal 客户异常排名没有执行人列，
  // 改为弹窗展示该人员在「人员异常排名」表中的自身行（包含负责客户数、高风险客户数等）。
  if (sourceTitle.includes('人员异常排名') && PERSON_COLS.includes(column)) {
    const source = drills.find((d) => d.title.includes('人员异常排名'));
    if (!source) return null;
    const rows = source.data.filter((r) =>
      PERSON_COLS.some((c) => String(r[c] ?? '') === v),
    );
    return {
      title: `${v} - 人员异常明细`,
      columns: source.columns,
      rows,
      block: source.block,
      filterKey: '人员',
    };
  }

  // 客户综合评估：人员排名（无对应客户明细按人员过滤的列），点击执行人后弹窗显示该人员在人员排名中的自身行
  if (sourceTitle.includes('人员排名') && PERSON_COLS.includes(column) && !sourceTitle.includes('人员每日明细')) {
    const source = drills.find((d) => d.title.includes('人员排名'));
    if (source) {
      const rows = source.data.filter((r) =>
        PERSON_COLS.some((c) => String(r[c] ?? '') === v),
      );
      return {
        title: `${v} - 人员排名详情`,
        columns: source.columns,
        rows,
        block: source.block,
        filterKey: '人员',
      };
    }
  }

  // 通用省办下钻 → 客户明细
  if (column === '省办' && isSourceDrillable(sourceTitle)) {
    const target = drills.find((d) => d.title.includes('客户明细'));
    if (target) {
      const rows = target.data.filter((r) => String(r['省办'] ?? '') === v);
      return {
        title: `${v} - 营业所+客户明细`,
        columns: target.columns,
        rows,
        block: target.block,
        filterKey: '省办',
      };
    }
  }

  // 通用人员下钻 → 优先人员每日明细，其次客户明细
  if (PERSON_COLS.includes(column) && isSourceDrillable(sourceTitle)) {
    const dailyTarget = drills.find((d) => d.title.includes('人员每日明细'));
    if (dailyTarget) {
      const rows = dailyTarget.data.filter((r) => {
        const candidate = r[column] ?? r['人员'] ?? r['姓名'] ?? r['执行人'];
        return String(candidate ?? '') === v;
      });
      return {
        title: `${v} - 每日明细`,
        columns: dailyTarget.columns,
        rows,
        block: dailyTarget.block,
        filterKey: '人员',
      };
    }
    const customerTarget = drills.find((d) => d.title.includes('客户明细'));
    if (customerTarget) {
      const rows = customerTarget.data.filter((r) => {
        const candidate = r[column] ?? r['人员'] ?? r['姓名'] ?? r['执行人'];
        return String(candidate ?? '') === v;
      });
      return {
        title: `${v} - 负责客户明细`,
        columns: customerTarget.columns,
        rows,
        block: customerTarget.block,
        filterKey: '人员',
      };
    }
  }

  return null;
}

interface TabPageProps {
  chartTitle?: string;
  chartFullKeys?: string[];
  showDrills?: boolean;
  showAbnormal?: boolean;
  abnormalAsTable?: boolean;
  /** 针对特定 drill 自定义渲染；返回非 null 时替代默认 DrillTable */
  customDrillRenderer?: (drill: DrillTableData) => React.ReactNode | null;
}

export default function TabPage({
  chartTitle = '趋势 / 结构 / 排名',
  chartFullKeys = [],
  showDrills = true,
  showAbnormal = true,
  abnormalAsTable = true,
  customDrillRenderer,
}: TabPageProps) {
  const { tabData, loading, error, currentTab, openModal, loadMore, refresh, sortBlock, drillBlock } = useDashboard();
  const filters = useDashboardStore((s) => s.filters);
  const data = tabData[currentTab];
  const hasActiveFilters = activeFilterCount(filters) > 0;

  if (loading) return <div className="loading">加载中...</div>;
  if (error)
    return (
      <div className="error">
        <span>加载失败：{error}</span>
        <button type="button" className="refresh-btn" onClick={refresh}>
          刷新重试
        </button>
      </div>
    );
  // tab 数据尚未加载完成时统一显示 loading，避免切换 tab 瞬间闪现暂无数据
  if (!data) return <div className="loading">加载中...</div>;

  const chartKeys = Object.keys(data.charts || {});
  const drills = data.drills || [];
  const abnormal = data.abnormal;

  // 被汇总表下钻引用的明细 drill（如「客户明细」「人员每日明细」）默认不平铺展示
  const hiddenDrillTitles = getHiddenDrillTitles(drills);
  const visibleDrills = drills.filter((d) => !hiddenDrillTitles.some((h) => d.title.includes(h)));

  /** 把块标识转成 loadMore 回调：有块标识的表才支持独立「加载更多」 */
  const blockLoader = (blockKey?: string) =>
    blockKey ? () => loadMore(currentTab, blockKey) : undefined;

  /** 点击下钻列时，把 UI 列名映射到 DashboardFilters 的维度键 */
  function drillFilterKey(column: string): string | null {
    if (column === '省办') return '省办';
    if (column === '人员' || column === '姓名' || column === '执行人') return '人员';
    return null;
  }

  return (
    <div className="container">
      {hasActiveFilters && (
        <div className="filter-scope-hint">
          当前已启用明细筛选，本页顶部 KPI/图表为全国口径快照；下方明细表已按筛选条件过滤。
        </div>
      )}
      <div data-block-key="kpi">
        <KPICards kpi={data.kpi} />
      </div>

      {chartKeys.length > 0 && (
        <div className="section">
          <div className="section-title">{chartTitle}</div>
          <div className="chart-row">
            {chartKeys.map((key) => {
              const chart = data.charts![key];
              const isFull = chartFullKeys.includes(key);
              // key 必须带 tab 前缀，防止不同 tab 的同名 chartKey 复用 ECharts 实例
              return (
                <div key={`${currentTab}-${key}`} data-block-key={`chart:${key}`} style={{ flex: isFull ? '1 1 100%' : '1 1 45%' }}>
                  <ChartCard
                    chartKey={`${currentTab}-${key}`}
                    chart={chart}
                    fullWidth={isFull}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showDrills &&
        visibleDrills.map((drill, idx) => {
          const custom = customDrillRenderer?.(drill);
          if (custom) {
            return (
              <div key={drill.block ?? drill.title ?? idx} data-block-key={drill.block}>
                {custom}
              </div>
            );
          }
          return (
            <div key={drill.block ?? drill.title ?? idx} data-block-key={drill.block}>
              <DrillTable
                title={drill.title}
                columns={drill.columns}
                data={drill.data}
                total={drill.total}
                onLoadMore={blockLoader(drill.block)}
                onSort={
                  drill.block
                    ? (column, direction) => sortBlock(currentTab, drill.block!, column, direction)
                    : undefined
                }
                onCustomerClick={(name) => {
                  // 预留：点击客户可打开详情弹窗
                  openModal(`客户详情：${name}`, [], []);
                }}
                onCellClick={async (column, value) => {
                  const target = findDrillTarget(data, drill.title, column, value);
                  if (!target) return;
                  // 本地已加载数据中有匹配记录，直接弹窗
                  if (target.rows.length > 0) {
                    openModal(target.title, target.columns, target.rows);
                    return;
                  }
                  // 本地没命中（目标块只加载了前 500 条），按点击维度请求后端过滤
                  if (target.block) {
                    const filterKey = target.filterKey || drillFilterKey(column);
                    if (!filterKey) return;
                    const res = await drillBlock(currentTab, target.block, { [filterKey]: String(value) });
                    if (res && res.rows.length > 0) {
                      openModal(target.title, target.columns, res.rows);
                    } else if (res) {
                      // 后端也没命中，给用户一个明确提示而不是无反应
                      openModal(target.title, target.columns, []);
                    }
                  }
                }}
              />
            </div>
          );
        })}

      {showAbnormal && abnormal && abnormalAsTable && abnormal.data && (
        Array.isArray(abnormal.data) ? (
          abnormal.data.length > 0 && (
            <div data-block-key={abnormal.block}>
              <DrillTable
                title={abnormal.title || '异常明细'}
                columns={abnormal.columns}
                data={abnormal.data}
                total={abnormal.total}
                onLoadMore={blockLoader(abnormal.block)}
                onSort={
                  abnormal.block
                    ? (column, direction) => sortBlock(currentTab, abnormal.block!, column, direction)
                    : undefined
                }
                onCustomerClick={(name) => openModal(`客户详情：${name}`, [], [])}
              />
            </div>
          )
        ) : (
          Object.entries(abnormal.data).map(([key, rows]) =>
            Array.isArray(rows) && rows.length > 0 ? (
              <div key={key} data-block-key={abnormal.block_keys?.[key]}>
                <DrillTable
                  title={`${abnormal.title || '异常明细'} - ${key}`}
                  columns={abnormal.columns}
                  data={rows}
                  total={abnormal.totals?.[key]}
                  onLoadMore={blockLoader(abnormal.block_keys?.[key])}
                  onSort={
                    abnormal.block_keys?.[key]
                      ? (column, direction) =>
                          sortBlock(currentTab, abnormal.block_keys![key], column, direction)
                      : undefined
                  }
                  onCustomerClick={(name) => openModal(`客户详情：${name}`, [], [])}
                />
              </div>
            ) : null,
          )
        )
      )}
    </div>
  );
}
